import React, { useEffect, useState } from "react";
import { Search, Trash2, CheckCircle, TriangleAlert } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import LogButton from "../common/LogButton";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/Category/CategoryTableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

type StatusType = "Active" | "Inactive";

interface Category {
  id: number;
  prod_cat_name: string;
  status: StatusType;
  log: string;
  is_parent_category: number;
  parent_category: string;
}

const CategoryTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [categorys, setCategorys] = useState<Category[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Category | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);

  const token = sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchCategorys = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) throw new Error("Unauthorized: Please log in.");
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/category/search/list`,
          { search: "" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === 200) {
          const formattedCategorys: Category[] = response.data.data
            .map((category: any) => ({
              id: category.id,
              prod_cat_name: category.prod_cat_name || "Unnamed",
              status: category.status === 1 ? "Active" : "Inactive",
              log: category.log || "",
              is_parent_category: category.is_parent_category || 0,
              parent_category: category.is_parent_category === 1 ? "Yes" : "No",
            }))
            .sort((a: Category, b: Category) => b.id - a.id);
          setCategorys(formattedCategorys);
        } else {
          setCategorys([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setCategorys([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategorys();
  }, [token]);

  const toggleStatus = async (id: number) => {
    try {
      if (!token) throw new Error("Authentication error! Please log in.");
      const category = categorys.find((s) => s.id === id);
      if (!category) return;
      const newStatus = category.status === "Active" ? 0 : 1;
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/category/status/update`,
        { pro_cat_id: id, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === 200) {
        setCategorys((prev) =>
          prev.map((s) =>
            s.id === id
              ? {
                  ...s,
                  status: newStatus === 1 ? "Active" : "Inactive",
                }
              : s
          )
        );
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to update status.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to update status."
      );
      setErrorModalOpen(true);
    }
  };

  const handleDeleteClick = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    openModal();
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategoryId || !token) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/category/delete`,
        { pro_cat_id: selectedCategoryId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === 200) {
        setCategorys((prev) => {
          const updated = prev.filter((s) => s.id !== selectedCategoryId);
          if (updated.length > 0 && displayedCategorys.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updated;
        });
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        closeModal();
      } else {
        throw new Error(response.data.message || "Failed to delete category.");
      }
    } catch (err) {
      console.error(err);
      const errorMsg =
        err instanceof Error ? err.message : "Failed to delete category.";
      closeModal(); // Close the confirmation modal immediately
      if (
        errorMsg ===
        "Can't delete this category because it's assigned to subcategory."
      ) {
        window.location.href = "/category-list"; // Redirect without showing error modal
      } else {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true); // Show error modal for other errors
      }
    }
  };
  const handleSort = (column: keyof Category) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredCategorys = categorys.filter((category) =>
    category.prod_cat_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCategorys = [...filteredCategorys].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortOrder === "asc"
      ? (valA as number) - (valB as number)
      : (valB as number) - (valA as number);
  });

  const displayedCategorys = sortedCategorys.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredCategorys.length / rowsPerPage);

  const handleDownload = () => {
    const data = searchTerm
      ? filteredCategorys
      : sortColumn
      ? sortedCategorys
      : categorys;

    if (!data.length) return;

    const formattedData = data.map((category) => ({
      "Category Name": category.prod_cat_name,
      Status: category.status,
      "Parent Category": category.parent_category,
      Log: category.log,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["Category List"]], { origin: "A1" });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) => String(row[key as keyof typeof row])?.length || 0
          ),
          "Category List".length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Categorys");
    XLSX.writeFile(
      workbook,
      searchTerm ? `CategoryData_${searchTerm}.xlsx` : "CategoryData.xlsx"
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Category List</h3>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(val) => {
              setRowsPerPage(val);
              setCurrentPage(1);
            }}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/category-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Category
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.th}
                  onClick={() => handleSort("prod_cat_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Status</p>
                </th>
                <th
                  className={styles.th}
                  onClick={() => handleSort("parent_category")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Parent Category</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    <Loader />
                  </td>
                </tr>
              ) : displayedCategorys.length ? (
                displayedCategorys.map((category) => (
                  <tr key={category.id} className={styles.tbodyRow}>
                    <td className={styles.td}>{category.prod_cat_name}</td>
                    <td className={styles.td}>
                      <div className={styles.statusContainer}>
                        <button
                          onClick={() => toggleStatus(category.id)}
                          className={`${styles.statusToggle} ${
                            category.status === "Active"
                              ? styles.statusToggleActive
                              : styles.statusToggleInactive
                          }`}
                        >
                          <div
                            className={`${styles.statusToggleCircle} ${
                              category.status === "Active"
                                ? styles.statusToggleCircleActive
                                : styles.statusToggleCircleInactive
                            }`}
                          ></div>
                        </button>
                        <span
                          className={`${styles.statusBadge} ${
                            category.status === "Active"
                              ? styles.statusActive
                              : styles.statusInactive
                          }`}
                        >
                          {category.status}
                        </span>
                      </div>
                    </td>
                    <td className={styles.td}>{category.parent_category}</td>
                    <td className={styles.td}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                        <Link
                          to={`/category-edit/${category.id}`}
                          className="inline-flex"
                        >
                          <button className={styles.editButton}>
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="text-white text-sm"
                            />
                          </button>
                        </Link>
                        </Tooltip>
                        <Tooltip content="Delete" bgColor="bg-red-600">
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteClick(category.id)}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-white text-sm"
                          />
                        </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <LogButton log={category.log} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className={styles.noDataText}>
                    No records found...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredCategorys.length}
          handlePageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <div className={styles.modalIconContainer}>
          <Trash2 className={styles.modalIcon} />
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this category?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Category Status Updated Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteSuccessModalOpen}
        onClose={() => setDeleteSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Category Deleted Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setDeleteSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isErrorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-red-100 dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-yellow-200 text-yellow-600 rounded-full">
            <TriangleAlert className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Alert!
        </h4>
        <p className="text-gray-600 dark:text-gray-300 mt-2">{errorMessage}</p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setErrorModalOpen(false)}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CategoryTable;
